import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const UsersPage = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const queryClient = useQueryClient();

  // Fetch users (now includes entities to reduce API calls)
  const { data: usersData, isLoading } = useQuery(
    ['admin-users', { search: searchText, role: roleFilter }],
    async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('email', searchText); // Updated to match backend parameter
      if (roleFilter) params.append('role', roleFilter);

      const response = await apiClient.get(`/api/admin/users?${params.toString()}`);
      return response.data.data;
    }
  );

  // Extract entities from users response (no separate API call needed)
  const entities = usersData?.entities || [];


  // Create user mutation
  const createUserMutation = useMutation(
    async (userData) => {
      const response = await apiClient.post('/api/admin/users', userData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User created successfully');
        queryClient.invalidateQueries('admin-users');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create user');
      },
    }
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    async ({ id, ...userData }) => {
      const response = await apiClient.put(`/api/admin/users/${id}`, userData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User updated successfully');
        queryClient.invalidateQueries('admin-users');
        setIsModalVisible(false);
        setEditingUser(null);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update user');
      },
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/api/admin/users/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User deleted successfully');
        queryClient.invalidateQueries('admin-users');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete user');
      },
    }
  );

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      entityIds: user.accessibleEntities?.map(entity => entity.id) || [],
      managerId: user.managerId,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteUserMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Transform entityIds to accessibleEntityIds for backend compatibility
      const transformedValues = {
        ...values,
        accessibleEntityIds: values.entityIds,
      };
      delete transformedValues.entityIds;

      if (editingUser) {
        updateUserMutation.mutate({ id: editingUser.id, ...transformedValues });
      } else {
        createUserMutation.mutate(transformedValues);
      }
    } catch (error) {
      // Silently handle form validation error
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'red';
      case 'Manager': return 'orange';
      case 'User': return 'blue';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag color={getRoleColor(role)}>{role}</Tag>,
      filters: [
        { text: 'Admin', value: 'Admin' },
        { text: 'Manager', value: 'Manager' },
        { text: 'User', value: 'User' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Manager',
      dataIndex: 'manager',
      key: 'manager',
      render: (manager) => manager ? manager.email : '-',
    },
    {
      title: 'Entity Access',
      dataIndex: 'accessibleEntities',
      key: 'entityAccess',
      render: (accessibleEntities) => (
        <div>
          {accessibleEntities?.length > 0 ? (
            <Tooltip title={accessibleEntities.map(entity => entity.name).join(', ')}>
              <Tag icon={<UserOutlined />} color="green">
                {accessibleEntities.length} entities
              </Tag>
            </Tooltip>
          ) : (
            <Tag color="default">No access</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>Users Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add User
          </Button>
        </div>

        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
          <Search
            placeholder="Search by email"
            allowClear
            style={{ width: 300 }}
            onSearch={setSearchText}
            onChange={(e) => !e.target.value && setSearchText('')}
          />
          <Select
            placeholder="Filter by role"
            allowClear
            style={{ width: 150 }}
            onChange={setRoleFilter}
          >
            <Option value="Admin">Admin</Option>
            <Option value="Manager">Manager</Option>
            <Option value="User">User</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={usersData?.users || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        confirmLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[
              { required: true, message: 'Please select a role' },
            ]}
          >
            <Select placeholder="Select role">
              <Option value="User">User</Option>
              <Option value="Manager">Manager</Option>
              <Option value="Admin">Admin</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="managerId"
            label="Manager"
          >
            <Select
              placeholder="Select manager (optional)"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {usersData?.users
                ?.filter(user => user.role === 'Manager' || user.role === 'Admin')
                ?.map(manager => (
                  <Option key={manager.id} value={manager.id}>
                    {manager.email} ({manager.role})
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="entityIds"
            label="Entity Access"
          >
            <Select
              mode="multiple"
              placeholder="Select entities for access"
              allowClear
            >
              {entities?.map(entity => (
                <Option key={entity.id} value={entity.id}>
                  {entity.name} ({entity.country})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;
